// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: pure placement for the Calendar's extra layers (Scheduled items and ClickUp due
// dates): which scheduled items fold into the "repeats" row, which become pins, how pins
// stack in a day column, and where a task's due date lands. No stores, so it can be tested.

import type {ScheduledItem, ScheduledKind} from '@app/features/konvo_scheduled/state/KonvoScheduled';

/** Kinds of Scheduled page items, in the order the Calendar's "Kinds of scheduled" menu lists them. */
export const SCHEDULED_KINDS: ReadonlyArray<ScheduledKind> = [
	'reminder',
	'message',
	'flow',
	'digest',
	'promise',
	'snooze',
];

/** What the Calendar draws. Mirrors the server's CalendarLayersPrefs. */
export interface CalendarLayers {
	meetings: boolean;
	scheduled: boolean;
	task_due: boolean;
	/** Scheduled kinds left out of the Scheduled layer. */
	hidden_kinds: Array<ScheduledKind>;
	/** Whose ClickUp due dates: tasks assigned to me, or everyone's. */
	due_scope: 'mine' | 'all';
}

/** The three layers the Show menu switches. */
export type CalendarLayerKey = 'meetings' | 'scheduled' | 'task_due';

export const DEFAULT_CALENDAR_LAYERS: CalendarLayers = {
	meetings: true,
	scheduled: false,
	task_due: false,
	hidden_kinds: [],
	due_scope: 'mine',
};

/** Same rules as the server's sanitizeCalendarLayers: meetings show unless switched off, the rest need a real `true`. */
export function sanitizeCalendarLayers(raw: unknown): CalendarLayers {
	const source = raw != null && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
	const rawKinds: Array<unknown> = Array.isArray(source.hidden_kinds) ? source.hidden_kinds : [];
	return {
		meetings: source.meetings !== false,
		scheduled: source.scheduled === true,
		task_due: source.task_due === true,
		hidden_kinds: SCHEDULED_KINDS.filter((kind) => rawKinds.includes(kind)),
		due_scope: source.due_scope === 'all' ? 'all' : 'mine',
	};
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Local YYYY-MM-DD of an instant. */
export function localDayKey(ms: number): string {
	const date = new Date(ms);
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function localMidnight(ms: number): number {
	const date = new Date(ms);
	date.setHours(0, 0, 0, 0);
	return date.getTime();
}

/** Minutes since local midnight. */
export function minutesOfDay(ms: number): number {
	const date = new Date(ms);
	return date.getHours() * 60 + date.getMinutes();
}

// ── Scheduled ───────────────────────────────────────────────────────────

export type RepeatRecurrence = 'weekdays' | 'daily';

/** Every weekday / every day items fold into one row instead of a pin in every column. */
export function isFoldedRepeat(item: Pick<ScheduledItem, 'recurrence'>): boolean {
	return item.recurrence === 'weekdays' || item.recurrence === 'daily';
}

export interface RepeatEntry {
	id: string;
	/** Local minutes since midnight of its first occurrence in the window. */
	minutes: number;
	item: ScheduledItem;
}

export interface RepeatGroup {
	recurrence: RepeatRecurrence;
	entries: Array<RepeatEntry>;
}

export interface ScheduledSplit {
	/** Weekday repeats first, then daily ones; entries by time of day. */
	repeats: Array<RepeatGroup>;
	/** One-off, weekly and monthly occurrences, soonest first. */
	pins: Array<ScheduledItem>;
}

/** Splits a window's occurrences into the repeats row and the pins. */
export function splitScheduled(items: ReadonlyArray<ScheduledItem>): ScheduledSplit {
	const pins: Array<ScheduledItem> = [];
	const firstById = new Map<string, ScheduledItem>();
	for (const item of items) {
		const at = Date.parse(item.at);
		if (Number.isNaN(at)) continue;
		if (!isFoldedRepeat(item)) {
			pins.push(item);
			continue;
		}
		const key = `${item.kind}:${item.id}`;
		const seen = firstById.get(key);
		if (!seen || Date.parse(seen.at) > at) firstById.set(key, item);
	}
	pins.sort((left, right) => Date.parse(left.at) - Date.parse(right.at));
	const repeats: Array<RepeatGroup> = [];
	for (const recurrence of ['weekdays', 'daily'] as const) {
		const entries = Array.from(firstById.values())
			.filter((item) => item.recurrence === recurrence)
			.map((item) => ({id: item.id, minutes: minutesOfDay(Date.parse(item.at)), item}))
			.sort((left, right) => left.minutes - right.minutes || left.item.title.localeCompare(right.item.title));
		if (entries.length > 0) repeats.push({recurrence, entries});
	}
	return {repeats, pins};
}

// ── Task due dates ─────────────────────────────────────────────────────

/** The parts of a ClickUp task the due layer reads. */
export interface DueTaskInput {
	id: string | null;
	name: string;
	due_date: number | null;
	/** False for date-only due dates (ClickUp keeps a placeholder time on them). */
	due_date_time: boolean;
	status: {type: string | null};
}

export interface TaskDue<T extends DueTaskInput = DueTaskInput> {
	task: T;
	id: string;
	/** The due instant; local midnight of the due day for date-only ones. */
	at: number;
	allDay: boolean;
}

function isClosed(task: DueTaskInput): boolean {
	return task.status.type === 'closed' || task.status.type === 'done';
}

/** Open tasks due in [from, to), soonest first; date-only ones sit at the start of their day. */
export function taskDuePlacements<T extends DueTaskInput>(
	tasks: ReadonlyArray<T>,
	from: number,
	to: number,
): Array<TaskDue<T>> {
	const seen = new Set<string>();
	const placed: Array<TaskDue<T>> = [];
	for (const task of tasks) {
		if (task.id == null || task.due_date == null || isClosed(task) || seen.has(task.id)) continue;
		const allDay = task.due_date_time !== true;
		const at = allDay ? localMidnight(task.due_date) : task.due_date;
		if (at < from || at >= to) continue;
		seen.add(task.id);
		placed.push({task, id: task.id, at, allDay});
	}
	return placed.sort(
		(left, right) =>
			left.at - right.at || Number(right.allDay) - Number(left.allDay) || left.task.name.localeCompare(right.task.name),
	);
}

// ── Placement helpers ──────────────────────────────────────────────────

/** Items grouped by local day, each day in the given order. */
export function bucketByDay<T>(items: ReadonlyArray<T>, at: (item: T) => number): Map<string, Array<T>> {
	const days = new Map<string, Array<T>>();
	for (const item of items) {
		const key = localDayKey(at(item));
		const list = days.get(key);
		if (list) list.push(item);
		else days.set(key, [item]);
	}
	return days;
}

/** Items on the local day that starts at `dayStart`. */
export function onDay<T>(items: ReadonlyArray<T>, at: (item: T) => number, dayStart: number): Array<T> {
	const start = localMidnight(dayStart);
	const end = localMidnight(start + DAY_MS + 2 * 60 * 60 * 1000);
	return items.filter((item) => {
		const ms = at(item);
		return ms >= start && ms < end;
	});
}

/**
 * Tops (in minutes since midnight) for slim pins in one day column: each at its time,
 * pushed down just enough not to overlap the pin above it.
 */
export function stackPins(
	pins: ReadonlyArray<{key: string; minutes: number}>,
	heightMinutes: number,
	gapMinutes = 0,
): Map<string, number> {
	const tops = new Map<string, number>();
	let floor = Number.NEGATIVE_INFINITY;
	for (const pin of [...pins].sort(
		(left, right) => left.minutes - right.minutes || left.key.localeCompare(right.key),
	)) {
		const top = Math.max(pin.minutes, floor);
		tops.set(pin.key, top);
		floor = top + heightMinutes + gapMinutes;
	}
	return tops;
}

export interface LaneSpan {
	id: string;
	start: number;
	end: number;
}

/**
 * Side-by-side lanes for things that overlap in a day column (meetings and pins alike):
 * each overlapping cluster splits the column into as many lanes as it needs at once.
 */
export function assignLanes(spans: ReadonlyArray<LaneSpan>): Map<string, {lane: number; lanes: number}> {
	const sorted = [...spans].sort((left, right) => left.start - right.start);
	const result = new Map<string, {lane: number; lanes: number}>();
	let cluster: Array<LaneSpan & {lane: number}> = [];
	let clusterEnd = Number.NEGATIVE_INFINITY;
	const flush = () => {
		const lanes = Math.max(1, ...cluster.map((item) => item.lane + 1));
		for (const item of cluster) result.set(item.id, {lane: item.lane, lanes});
		cluster = [];
	};
	for (const span of sorted) {
		if (cluster.length > 0 && span.start >= clusterEnd) flush();
		const used = new Set(cluster.filter((other) => other.end > span.start).map((other) => other.lane));
		let lane = 0;
		while (used.has(lane)) lane += 1;
		cluster.push({...span, lane});
		clusterEnd = Math.max(clusterEnd, span.end);
	}
	flush();
	return result;
}

/** Agenda order: all-day things first, then by time; at the same time meetings lead. */
export function compareAgenda(
	left: {at: number; allDay: boolean; rank?: number},
	right: {at: number; allDay: boolean; rank?: number},
): number {
	return Number(right.allDay) - Number(left.allDay) || left.at - right.at || (left.rank ?? 0) - (right.rank ?? 0);
}

/** How many of `extra` fit in a month cell that already shows `taken` meetings, and how many are left over. */
export function fitMonthCell(taken: number, extra: number, limit: number): {shown: number; more: number} {
	const room = Math.max(0, limit - taken);
	const shown = Math.min(room, extra);
	return {shown, more: extra - shown};
}
